import { SheetWithFormComponent } from '@/components/ui/SheetWithFormComponent'
import { useRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { ErrorComponent } from '@/ui-legacy/ErrorComponent'
import { FormComponentLoading } from '@/catalog-apps/components/component-library/FormComponent'

// Shadcn components
import { Button } from '@/components/shadcn/button'
import { Form } from '@/components/shadcn/form'
import { MultiSelectComboboxField, SelectOption } from '@/components/form-components/MultiSelectCombobox'
import { IconDeleteOutline } from '@icons/IconDeleteOutline'
import { Badge } from '@/components/shadcn/badge'
import { useCurrentOrganisation } from '../useCurrentOrganisation'
import { Avatar, AvatarFallback } from '@/components/shadcn/avatar'
import { getInitials } from '@/lib/utils'

// Form validation schema
const formSchema = z.object({
  selectedUserIds: z.array(z.number()).min(1, 'Please select at least one team member to add'),
})

export type MaintainProjectTeamForm = z.infer<typeof formSchema>

type MessagesSubscriptionsOrganisationUser = RestSchemas['Messages.Subscriptions.OrganisationUser']
type MessagesSubscriptionsOrganisationTeam = RestSchemas['Messages.Subscriptions.OrganisationTeam']
type MessagesSubscriptionsOrganisationTeamUser = RestSchemas['Messages.Subscriptions.OrganisationTeamUser']

type MaintainProjectTeamFormProps = {
  onSubmit?: () => void
  sheetTrigger?: React.ReactNode
  sheetTriggerTooltip?: string
  sheetTitle?: string
  sheetDescription?: string
  openInSheet?: boolean
  selectedProjectTeam: MessagesSubscriptionsOrganisationTeam
}

export const MaintainProjectTeamForm = (props: MaintainProjectTeamFormProps) => {
  const [sheetOpen, setSheetOpen] = useState(false)

  return props.sheetTrigger || props.openInSheet ? (
    <SheetWithFormComponent
      title={props.sheetTitle}
      description={props.sheetDescription}
      trigger={props.sheetTrigger}
      triggerTooltip={props.sheetTriggerTooltip}
      wide
      isOpen={sheetOpen}
      onOpenChange={setSheetOpen}
    >
      <FormComponent {...props} />
    </SheetWithFormComponent>
  ) : (
    <FormComponent {...props} />
  )
}

const FormComponent = (props: MaintainProjectTeamFormProps) => {
  const { id: orgId } = useCurrentOrganisation()

  const {
    getSubscriptionsApiListOrganisationUsers,
    getSubscriptionsApiListTeamUsers,
    postSubscriptionsApiAddTeamUser,
    deleteSubscriptionsApiDeleteTeamUser,
    getSubscriptionsApiGetOrganisationTeams,
  } = useRestClient()

  const {
    data: allOrgUsers,
    isPending: allOrgUsersLoading,
    isError: allOrgUsersError,
  } = getSubscriptionsApiListOrganisationUsers.useQuery({
    query: { orgId: orgId ?? props.selectedProjectTeam?.organisationId ?? 0 },
  })

  const {
    data: currentTeamUsers,
    isPending: currentTeamUsersLoading,
    isError: currentTeamUsersError,
  } = getSubscriptionsApiListTeamUsers.useQuery(
    { query: { teamId: props.selectedProjectTeam?.id } },
    {
      select: (data) =>
        data.filter(
          (user) =>
            user.userId &&
            user.userId !== 0 &&
            user.id &&
            user.id !== 0 &&
            user.teamId === props.selectedProjectTeam?.id
        ),
      enabled: !!props.selectedProjectTeam?.id,
    }
  )

  const { mutateAsync: addTeamUser } = postSubscriptionsApiAddTeamUser.useMutation(undefined, {
    onSuccess: () => {
      // Invalidate multiple queries
      getSubscriptionsApiGetOrganisationTeams.invalidateQueries({
        parameters: { query: { orgId: props.selectedProjectTeam?.organisationId || 0 } },
      })
      getSubscriptionsApiListOrganisationUsers.invalidateQueries({
        parameters: { query: { orgId: orgId ?? props.selectedProjectTeam?.organisationId ?? 0 } },
      })
      getSubscriptionsApiListTeamUsers.invalidateQueries({
        parameters: { query: { teamId: props.selectedProjectTeam?.id } },
      })
      toast.success('Team members successfully added')
      props.onSubmit?.()
    },
    onError: () => {
      toast.error('Error adding team members')
    },
  })

  const { mutate: deleteTeamUser } = deleteSubscriptionsApiDeleteTeamUser.useMutation(undefined, {
    onSuccess: () => {
      // Invalidate multiple queries
      getSubscriptionsApiGetOrganisationTeams.invalidateQueries({
        parameters: { query: { orgId: props.selectedProjectTeam?.organisationId || 0 } },
      })
      getSubscriptionsApiListOrganisationUsers.invalidateQueries({
        parameters: { query: { orgId: orgId ?? props.selectedProjectTeam?.organisationId ?? 0 } },
      })
      getSubscriptionsApiListTeamUsers.invalidateQueries({
        parameters: { query: { teamId: props.selectedProjectTeam?.id } },
      })
      toast.success('Team member successfully removed')
    },
    onError: () => {
      toast.error('Error removing team member')
    },
  })

  const defaultValues = useMemo(() => {
    return {
      selectedUserIds: [] as number[],
    }
  }, [])

  const form = useForm<MaintainProjectTeamForm>({
    resolver: zodResolver(formSchema),
    defaultValues,
  })

  // Helper function to get user display name
  const getUserDisplayName = (user: MessagesSubscriptionsOrganisationUser): string => {
    return user.givenName || user.familyName
      ? `${user.givenName ?? ''} ${user.familyName ?? ''}`.trim()
      : (user.email ?? 'Unknown User')
  }

  // Create team member options with disambiguation for duplicate names
  const teamMemberOptions: SelectOption<number>[] = useMemo(() => {
    if (!allOrgUsers) return []

    // Sort users by family name
    const sortedUsers = [...allOrgUsers].sort((a, b) => {
      const aName = a.familyName || ''
      const bName = b.familyName || ''
      return aName.localeCompare(bName)
    })

    // Count name occurrences for disambiguation
    const nameMap: Record<string, number> = {}
    sortedUsers.forEach((user) => {
      const displayName = getUserDisplayName(user)
      nameMap[displayName] = (nameMap[displayName] || 0) + 1
    })

    // Create options with disambiguation when needed
    return sortedUsers
      .filter((user) => !currentTeamUsers?.some((teamUser) => teamUser.userId === user.id))
      .map((user) => {
        const baseName = getUserDisplayName(user)
        const label = nameMap[baseName] > 1 ? `${baseName} (ID: ${user.id})` : baseName

        return {
          value: user.id,
          label,
        }
      })
  }, [allOrgUsers, currentTeamUsers])

  const handleSubmit = async (data: MaintainProjectTeamForm) => {
    const promises = data.selectedUserIds.map((userId) =>
      addTeamUser({
        body: {
          userId,
          teamId: props.selectedProjectTeam.id,
        },
      })
    )
    await Promise.all(promises)
    form.reset() // Reset form after submission
  }

  const handleDeleteTeamUser = (teamUser: MessagesSubscriptionsOrganisationTeamUser) => {
    deleteTeamUser({
      body: {
        ...teamUser,
        teamId: props.selectedProjectTeam?.id ?? 0,
      },
    })
  }

  if (allOrgUsersLoading || currentTeamUsersLoading) return <FormComponentLoading />
  if (allOrgUsersError || currentTeamUsersError) return <ErrorComponent />

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <div className="flex h-full flex-col">
            <div className="space-y-6">
              <MultiSelectComboboxField
                control={form.control}
                name="selectedUserIds"
                label="Add Team Members"
                options={teamMemberOptions}
                placeholder="Select team members to add..."
                searchPlaceholder="Search team members..."
                emptyMessage="No available team members found."
              />
            </div>
            <div className="form-action-container">
              <Button type="submit" disabled={teamMemberOptions.length === 0}>
                Add Selected Members
              </Button>
            </div>
          </div>
        </form>
      </Form>
      {currentTeamUsers && currentTeamUsers.length > 0 && (
        <div className="space-y-4">
          <div className="text-sm font-medium">Current Team Members:</div>
          <div className="space-y-2">
            {currentTeamUsers.map((teamUser) => (
              <TeamMemberCard
                key={`${teamUser.id}-${teamUser.teamId}-${teamUser.userId}`}
                teamUser={teamUser}
                onDelete={() => handleDeleteTeamUser(teamUser)}
              />
            ))}
          </div>
        </div>
      )}
    </>
  )
}

// Team member card component
const TeamMemberCard = ({
  teamUser,
  onDelete,
}: {
  teamUser: MessagesSubscriptionsOrganisationTeamUser
  onDelete: () => void
}) => {
  const displayName =
    teamUser.givenName || teamUser.familyName
      ? `${teamUser.givenName ?? ''} ${teamUser.familyName ?? ''}`.trim()
      : (teamUser.email ?? 'Unknown User')

  return (
    <div className="flex items-center justify-between rounded-lg border bg-background p-3">
      <div className="flex items-center gap-5">
        <Avatar className="ring-2 ring-background">
          <AvatarFallback className="bg-accent text-sm text-accent-foreground">
            {getInitials(displayName)}
          </AvatarFallback>
        </Avatar>
        <div className="flex items-center gap-2 text-sm">
          <span className="font-medium">{displayName}</span>
          <Badge variant="outline">{teamUser.userId}</Badge>
        </div>
      </div>
      <Button variant="ghost" size="icon" onClick={onDelete}>
        <IconDeleteOutline />
      </Button>
    </div>
  )
}
